import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import { useSnackbar } from "notistack";
import { API_URL } from "../../common/AppConstants";
import { getSecretByReservationId } from "../../api/Stripe";
import { Elements } from "@stripe/react-stripe-js";
import { useCustomStripe } from "../../common/Providers/CustomStripeProvider/UseCustomStripe";
import { useStoreDetails } from "../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import CheckoutForm from "../CheckoutForm";
import { activateBodhiProtect, expirePublicLink } from "../../api/Product";
import { useNavigate } from "react-router";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EmailIcon from "@mui/icons-material/Email";
import BodhiProtectSelector from "../../components/BodhiProtectSelector";

const BodhiProtectPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { storeContent, storeDetails } = useStoreDetails();
  const navigate = useNavigate();

  const { clientSecret, stripePromise, setClientSecret } = useCustomStripe();

  const [protectionSelection, setProtectionSelection] = useState<boolean>(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatPrice = (price: number | undefined | null) => {
    const value = typeof price === "number" ? price : 0;
    return `$${value.toFixed(2)}`;
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [reservation, setReservation] = useState<any | null>(null);

  const groupedItems = useMemo(() => {
    if (!reservation?.items || reservation.items.length === 0) return [];

    const grouped = reservation.items.reduce(
      (acc: Record<string, any>, item: any) => {
        const key = item.display_name || item.family;
        if (acc[key]) {
          acc[key].quantity += item.quantity || 0;
        } else {
          acc[key] = {
            ...item,
            quantity: item.quantity || 0,
          };
        }
        return acc;
      },
      {} as Record<string, any>
    );

    return Object.values(grouped);
  }, [reservation?.items]);

  const protectionPrice = useMemo(() => {
    if (!reservation?.pricing?.subtotal) return 0;
    const percentage = parseFloat(storeDetails.bodhi_protect_percentage || "0");
    if (Number.isNaN(percentage)) return 0;
    return reservation.pricing.subtotal * (percentage / 100);
  }, [reservation?.pricing?.subtotal, storeDetails.bodhi_protect_percentage]);

  const protectionOptions = useMemo(
    () => [
      {
        value: true,
        label: `Yes, protect my booking (Add ${formatPrice(protectionPrice)})`,
      },
      {
        value: false,
        label: "No thanks, I'll take the risk",
      },
    ],
    [protectionPrice]
  );

  const handleProtectionYes = () => {
    setProtectionSelection(true);
  };

  const handleProtectionNo = () => {
    // user clicked the "No" card -> just open dialog
    setConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    // user clicked "No" in dialog -> revert to Yes
    setConfirmOpen(false);
    setProtectionSelection(true);
  };

  const handleConfirmYes = async () => {
    // TODO: call your API to expire this link here
    await expirePublicLink({ customer_id: reservation.customer.id, token });

    // after successful API:
    setProtectionSelection(false);
    setConfirmOpen(false);

    // redirect to home page
    if (storeDetails.is_rental) {
      navigate("/rental");
    } else {
      navigate("/");
    }
  };

  // verify token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setError("Invalid or missing token");
      setLoading(false);
      return;
    }
    setToken(t);

    const verifyReservation = async () => {
      try {
        const response = await fetch(
          `${API_URL}/reservations/verifypubliclinkandgetreservation`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: t, includeDetails: true }),
          }
        );

        if (!response.ok) {
          // const err = await response.json().catch(() => null);
          const { error } = await response.json();
          throw new Error(error || "Failed to verify link");
        }

        const result = await response.json();
        const resData = result.data || {};
        setReservation(resData);
      } catch (err: any) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    verifyReservation();
  }, []);

  useEffect(() => {
    if (reservation?.reservation_id && protectionPrice > 0) {
      const protectionPriceStr = protectionPrice.toFixed(2);
      getSecretByReservationId(
        {
          reservation_id: reservation.reservation_id,
          amount: protectionPriceStr,
          customer_id: reservation.customer.id,
        },
        (jsonRes: any, status: any, error: any) => {
          if (status === 200) {
            setClientSecret(jsonRes);
          } else {
            const errorMessage =
              jsonRes?.raw?.message ?? "Error occured on the server.";
            enqueueSnackbar(errorMessage, {
              variant: "error",
              style: { width: "350px" },
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
          }
        }
      );
    }
  }, [reservation, protectionPrice]);

  const handleBeforeConfirmPayment = async (): Promise<number | null> => {
    // For Bodhi Protect standalone, we do NOT create a new reservation here.
    // We only return the existing reservation id so that onPaymentSuccess can use it if needed.
    if (!reservation?.reservation_id) return null;
    return reservation.reservation_id;
  };

  const handlePaymentSuccess = async ({
    reservationId,
    paymentIntentId,
    amount,
  }: {
    reservationId: number | null;
    paymentIntentId?: string;
    amount?: number;
  }) => {
    if (!reservationId || !token) {
      return;
    }

    await activateBodhiProtect(
      {
        reservation_id: reservationId,
        token,
        customer_id: reservation.customer.id,
        payment_intent_id: paymentIntentId,
        amount: amount,
      },
      () => {
        navigate("/bodhiprotect/thankyou");
      }
    );
  };

  const appearanceOptions = {
    rules: {
      ".Input": {
        borderColor: "#cccccc",
      },
    },
    layout: {
      type: "accordion",
      defaultCollapsed: true,
    },
  };

  const options = {
    clientSecret: clientSecret?.client_secret ?? "",
    appearance: appearanceOptions,
  };

  if (loading && !reservation) {
    return (
      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error && !reservation) {
    return (
      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      {" "}
      <Box
        sx={{
          backgroundColor: "#191919",
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            maxWidth: "960px",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
          }}
        >
          <Box sx={{ width: "100%", height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "white",
                padding: "16px 9px",
                borderRadius: "9px 9px 0 0",
                backgroundColor: storeContent.primary_color || "#1976d2",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "white", fontSize: "20px" }}
                >
                  {storeDetails?.store_name}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                backgroundColor: "#fff",
                p: 3,
              }}
            >
              {error && reservation && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              )}

              {reservation?.reservation_id && (
                <BodhiProtectSelector
                  options={protectionOptions}
                  selectedValue={protectionSelection}
                  onSelectYes={handleProtectionYes}
                  onSelectNo={handleProtectionNo}
                  primaryColor={storeContent.primary_color || "#1976d2"}
                />
              )}

              {/* <BodhiProtect /> */}
              {clientSecret && clientSecret.client_secret && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: "600px" }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 3, fontWeight: 600, color: "#333" }}
                    >
                      Payment Options
                    </Typography>
                    <Elements stripe={stripePromise} options={options}>
                      <CheckoutForm
                        mode="bodhi_protect"
                        onBeforeConfirmPayment={handleBeforeConfirmPayment}
                        onPaymentSuccess={handlePaymentSuccess}
                        returnUrlPath="/rental/thankyou"
                      />
                    </Elements>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              alignSelf: "flex-start",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              overflow: "hidden",
              backgroundColor: "#e6edf9",
              maxWidth: { xs: "100%", md: "296px" },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#333333",
                  marginBottom: 1,
                }}
              >
                Your Order
              </Typography>
              {reservation?.order_number && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginBottom: 1,
                  }}
                >
                  Order #{reservation.order_number}
                </Typography>
              )}
            </Box>

            <Divider sx={{ borderColor: "#ccc", borderWidth: 1 }} />

            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#1f2937",
                    marginBottom: "8px",
                  }}
                >
                  {reservation?.customer?.name || "Customer"}
                </Typography>

                {reservation?.customer?.email && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                    <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                      {reservation?.customer?.email}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ borderColor: "#ccc", borderWidth: 1, mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 16, color: "#6b7280", fontWeight: 400 }}
                  >
                    Bodhiprotect
                  </Typography>
                  <Typography
                    sx={{ fontSize: 16, color: "#1f2937", fontWeight: 600 }}
                  >
                    {formatPrice(protectionPrice)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 16, color: "#6b7280", fontWeight: 400 }}
                  >
                    Taxes & Fees
                  </Typography>
                  <Typography
                    sx={{ fontSize: 16, color: "#1f2937", fontWeight: 600 }}
                  >
                    {formatPrice(0)}
                  </Typography>
                </Box>

                <Divider
                  sx={{ borderColor: "#d1d5db", borderWidth: 1, my: 2 }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      color: "#1f2937",
                      fontWeight: 700,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 22,
                      color: "#2563eb",
                      fontWeight: 800,
                    }}
                  >
                    {formatPrice(protectionPrice)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: "#ccc", borderWidth: 1, mb: 2 }} />

              {(reservation?.start_date || reservation?.end_date) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "8px",
                    }}
                  >
                    Rental Details
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <CalendarTodayIcon
                      sx={{ fontSize: 18, color: "#666", mt: "2px" }}
                    />
                    <Box>
                      {reservation?.start_date && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#1f2937",
                            fontWeight: 600,
                          }}
                        >
                          {formatDate(reservation.start_date)}
                        </Typography>
                      )}
                      {reservation?.end_date && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#6b7280",
                            fontWeight: 400,
                          }}
                        >
                          Until {formatDate(reservation.end_date)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {groupedItems.length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "8px",
                    }}
                  >
                    Items
                  </Typography>
                  {groupedItems.map((item: any) => (
                    <Box
                      key={item.display_name || item.family || item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <DirectionsCarIcon sx={{ fontSize: 18, color: "#666" }} />
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#1f2937",
                          fontWeight: 600,
                        }}
                      >
                        {item.quantity}x {item.display_name || item.family}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog open={confirmOpen} onClose={handleConfirmCancel}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you don&apos;t want to protect your booking?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleConfirmCancel} color="inherit">
            No
          </Button>
          <Button
            onClick={handleConfirmYes}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BodhiProtectPage;
